import { useEffect, useState } from "react";
import styled, { css, keyframes } from "styled-components";
import { styled as muiStyled } from "@mui/material/styles";
import { useSnackbar } from "notistack";
import dayjs from "dayjs";

import {
  Autocomplete,
  Box,
  FormControl,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Stack,
} from "@mui/material";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import IconButton from "@mui/material/IconButton";
import MuiAccordion from "@mui/material/Accordion";
import MuiAccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import { DatePicker } from "@mui/x-date-pickers-v6";
import { LocalizationProvider } from "@mui/x-date-pickers-v6/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers-v6/AdapterDayjs";

import CloseIcon from "@mui/icons-material/Close";
import FileUploadIcon from "@mui/icons-material/FileUpload";
import DeleteIcon from "@mui/icons-material/Delete";
// import AddIcon from "@mui/icons-material/Add";
import InfoIcon from "@mui/icons-material/Info";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

import { updateShippingOrder } from "src/api/shippingOrder";
import { getCityInfo, getQuotationInfo, placeOrder } from "src/api/lalamove";

import font from "src/styles/font";
import device from "src/styles/device";
import { checkNotEmpty, convertPrice } from "src/common";
import { useDialog } from "src/context/DialogContext";
import {
  Black,
  DarkGrey,
  Grey,
  GreyStroke,
  Primary,
  White01,
  Yellow,
} from "src/styles/color";
import {
  serviceType,
  // categoriesText,
  // handlingInstructions,
} from "src/common/lalamove";

import Dialog from "src/components/core/Dialog";
import Button from "src/components/core/Button";
import TextField from "src/components/core/TextField";
// import UploadImage from "src/components/upload/dropzone/UploadImage";
import UploadMultiImage from "src/components/upload/dropzone/UploadMultiImage";
import QuantityInput from "src/components/ui/QuantityInput";
import CustomMap from "src/components/ui/Maps/CustomMaps";

import {
  RemarkText,
  SpanLabel,
  TextOption,
} from "src/modules/order/shipping-order/components";
import {
  MapsContainer,
  WrapperMapsContainer,
} from "src/components/order/components";
import SelectDateDialog from "src/components/order/SelectDateDialog";

const CloseButton = styled(IconButton)`
  &.MuiIconButton-root {
    color: ${Black};
  }
`;

const StyledDialog = muiStyled(Dialog)(({ theme }) => ({
  "& .MuiPaper-root": {
    borderRadius: 16,
  },
  "& .MuiDialogContent-root": {
    maxHeight: "500px",
    border: 0,
    padding: 24,
    paddingTop: 0,
  },
  "& .MuiDialog-container": {
    "& .MuiPaper-root": {
      width: "100%",
      maxWidth: 640,
    },
  },
  "& .MuiDialogActions-root": {
    justifyContent: "center",
  },
  "& .MuiDialogActions-root>:not(:first-of-type)": {
    marginLeft: 24,
  },

  [theme.breakpoints.down("sm")]: {
    "& .MuiPaper-root": {
      margin: 24,
    },
    "& .MuiDialogContent-root": {
      padding: 16,
      paddingTop: 0,
    },
    "& .MuiDialogActions-root": {
      padding: 16,
    },
  },
}));

const Line = styled.div`
  width: 100%;
  height: 1px;
  background-color: #d8d8dc;
  margin: 16px 0;
`;

const StyledDatePicker = styled(DatePicker)`
  width: 100%;

  .MuiOutlinedInput-root {
    padding-right: 12px;
  }
  & .MuiInputBase-input.MuiOutlinedInput-input {
    padding: 12px 16px;
  }
  &.MuiInputBase-root.Mui-focused .MuiOutlinedInput-notchedOutline {
    border-color: ${DarkGrey};
  }
  .Mui-disabled,
  &.Mui-disabled .MuiOutlinedInput-notchedOutline {
    border-color: ${GreyStroke};
  }
`;

const Accordion = styled(MuiAccordion)`
  &.MuiAccordion-root {
    border: 1px solid #0000003b;
    box-shadow: none;
  }

  &.MuiPaper-root.MuiAccordion-root:last-of-type,
  &.MuiPaper-root.MuiAccordion-root:first-of-type {
    border-radius: 16px;
  }

  &::before {
    display: none;
  }
`;

const AccordionSummary = styled(MuiAccordionSummary)`
  & .MuiAccordionSummary-content {
    font-size: ${font.size.md};
    font-weight: 400;
    color: #00000061;
  }

  &.MuiAccordionSummary-root.Mui-expanded {
    min-height: 60px;
  }
  & .MuiAccordionSummary-content.Mui-expanded {
    margin: 14px 0;
  }
`;

const Dragger = styled.div`
  width: 100%;
  padding: 24px 16px;
  position: relative;
  /* display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column; */
  border-radius: 16px;
  border: 1px dashed ${GreyStroke};
  text-align: center;
  background-color: #faf9f9;

  &:hover {
    border: 1px dashed ${Primary};
    cursor: pointer;
  }

  @media ${device.mobileL} {
    padding: 16px;
    border-radius: 8px;
  }
`;

const WrapperIconUpload = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  padding: 8px 8px;
  margin: 0 auto;
  background-color: ${White01};
`;

const TextDragger = styled.p`
  width: 100%;
  margin: 4px 0;
  font-size: 0.875rem;
  font-weight: 400;
  color: ${DarkGrey};

  span {
    font-size: 0.875rem;
    font-weight: 600;
    color: ${Black};
  }

  :first-of-type {
    margin-top: 12px;
  }

  @media ${device.mobileL} {
    font-size: 0.75rem;
  }
`;

const UploadItem = styled.div`
  width: 100%;
  padding: 12px;
  border: 1px solid ${Grey};
  border-radius: 16px;

  @media ${device.mobileL} {
    padding: 8px;
    border-radius: 8px;
  }
`;

const PreviewImage = styled.img`
  width: 80px;
  height: 80px;
  border-radius: 8px;
  object-fit: cover;
  object-position: center;

  @media ${device.mobileL} {
    width: 60px;
    height: 60px;
    border-radius: 4px;
  }
`;

const PreviewImageTitle = styled.div`
  font-size: ${font.size.md};
  font-weight: 400;
  line-height: 24px;
  color: Black;
  overflow-wrap: anywhere;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  overflow: hidden;
  white-space: normal;

  @media ${device.mobileL} {
    font-size: ${font.size.sm};
  }
`;

const WrapperCalOrder = styled.div`
  /* width: 100%; */
  background-color: ${White01};
  padding: 16px;
  border-radius: 16px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  margin: 0;
  gap: 16px;
  /* border-bottom: 1px solid #d9d9d9; */
`;

const RowSumPrice = styled.div`
  margin-left: 0;
  margin-right: 0;
  justify-content: space-between;
  align-items: center;
  display: flex;
`;

const Price = styled.span`
  color: ${(props) => (props.fontColor ? props.fontColor : "#252525")};
  font-size: 1.125rem;
  font-weight: 400;

  ${({ desc, total }) =>
    desc && total
      ? css`
          font-size: 1rem;
          font-weight: 600;
        `
      : desc
        ? css`
            font-size: 1rem;
          `
        : total
          ? css`
              font-weight: 500;
              color: ${Primary};
            `
          : null}
`;

const rainbowAnimation = keyframes`
    0%, 100% {
        background-position: 0 0;
    }

    50% {
        background-position: 100% 0;
    }
`;

const WarningText = styled.div`
  font-size: ${font.size.xs};
  font-weight: 400;
  color: ${Primary};

  /* background: linear-gradient(
    to right,
    #6666ff,
    #0099ff,
    #00ff00,
    #ff3399,
    #6666ff
  );
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
  animation: ${rainbowAnimation} 6s ease-in-out infinite;
  background-size: 400% 100%; */
`;

const DeliveringDialog = ({
  onClose,
  onSuccess,
  orderDetail,
  setIsLoading,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const { openDialog, closeDialog } = useDialog();

  const initialDetail = {
    order: {},
    method: "ส่งไปถึงลูกค้าโดยตรง",
    fulfillmentProcess: [
      {
        type: "มีค่าจัดส่ง",
        carrier: "จัดส่งโดยร้านค้า",
        trackingNumber: null,
        trackingUrl: null,
        shippingFee: null,
        pickupAddress: {
          name: null,
          tel: null,
          address: null,
          coordinates: {
            lat: null,
            lng: null,
          },
        },
        shippingProofs: [],
        deliveryProofs: [],
        shippedDate: null,
        estimatedDeliveryDate: null,
        actualDeliveryDate: null,
        remark: null,
      },
    ],
    payment: {
      method: null,
      info: {
        bankAccountName: null,
        bankNo: null,
      },
      total: null,
      paymentDate: null,
      paymentProof: null,
    },
    status: {
      failureReason: null,
      value: "pending",
    },
    note: null,
    documents: {
      customer: {
        qt: {
          qtDate: null,
          qtNo: null,
          customerQtFile: null,
          customerQtAdditionalFile: null,
        },
        inv: {
          invDate: null,
          invNo: null,
          customerInvFile: null,
          customerInvAdditionalFile: null,
        },
      },
      xsurface: {
        po: {
          poDate: null,
          poNo: null,
          xsurfacePoFile: null,
          xsurfacePoAdditionalFile: null,
        },
        vc: {
          vcDate: null,
          vcNo: null,
          xusrfaceVcFile: null,
          xsurfaceVcAdditionalFile: null,
        },
      },
      seller: {
        qt: {
          qtDate: null,
          qtNo: null,
          sellerQtFile: null,
          sellerQtAdditionalFile: null,
        },
        inv: {
          invDate: null,
          invNo: null,
          sellerInvFile: null,
          sellerInvAdditionalFile: null,
        },
      },
    },
  };
  const [shippingDetail, setShippingDetail] = useState(initialDetail);

  // lalamove
  const [selectedCities, setSelectedCities] = useState([]);
  const initialQuotation = {
    orderId: orderDetail?.order?._id || null,
    scheduleAt: null,
    serviceType: null,
    specialRequests: [], // optional
    language: "th_TH",
    stops: [
      {
        name: "",
        phone: "",
        coordinates: {
          lat: 13.6940782,
          lng: 100.6022862,
        },
        address: "",
        remark: "", // remove before send request
      },
    ],
    // รายการของที่ซื้อ
    item: {
      quantity: 1,
      weight: null,
      categories: [],
      handlingInstructions: [],
    },
    isRouteOptimized: true,
  };
  const [quotationPayload, setQuotationPayload] = useState(initialQuotation);

  const [isScheduleDayBefore, setIsScheduleDayBefore] = useState(false); // for check if schedule date is before current date/time
  const [isQuotationChanged, setIsQuotationChanged] = useState(false); // for check if quotation is changed
  const [isLalamoveFailed, setIsLalamoveFailed] = useState(false); // for check if lalamove request is failed

  const lalamoveFailedStatus = ["CANCELLED", "REJECTED", "EXPIRED"];
  const removeServiceType = [
    "MOTORCYCLE",
    "MOTORCYCLE_RENTAL",
    "MPV_INTERCITY",
    "SEDAN_INTERCITY",
    "SUV_INTERCITY",
    "LD_PICKUP_TRUCK",
    "LD_4_DOOR_PICKUP_TRUCK",
  ];

  const isJSONString = (str) => {
    if (typeof str !== "string") return str;
    try {
      return JSON.parse(str);
    } catch {
      return str;
    }
  };

  useEffect(() => {
    (async () => {
      if (!!orderDetail) {
        setShippingDetail((order) => ({
          ...order,
          ...orderDetail,
          method: orderDetail.method
            ? isJSONString(orderDetail.method)
            : "ส่งไปถึงลูกค้าโดยตรง",
          fulfillmentProcess:
            orderDetail.fulfillmentProcess.length > 0
              ? orderDetail.fulfillmentProcess
              : initialDetail.fulfillmentProcess,
          payment: orderDetail.payment,
          documents: orderDetail.documents,
        }));

        if (orderDetail?.fulfillmentProcess[0]?.type === "โดย Xsurface") {
          let citiesData = await fetchCities();
          let scheduleDate = null;
          let requestData = !!orderDetail?.fulfillmentProcess[0]
            ?.lalamoveRequest?.request
            ? orderDetail?.fulfillmentProcess[0]?.lalamoveRequest?.request // เคยเรียกรถแล้วแต่ยังไม่สำเร็จ / โดนยเลิก
            : orderDetail?.fulfillmentProcess[0]?.lalamoveCheck?.request;

          if (
            lalamoveFailedStatus.includes(
              orderDetail?.fulfillmentProcess[0]?.lalamoveRequest?.result
                ?.status,
            )
          ) {
            setIsLalamoveFailed(true);
          }

          // check if scheduleAt is before current date/time
          if (!!requestData?.scheduleAt) {
            if (dayjs(requestData?.scheduleAt).isBefore(dayjs(new Date()))) {
              scheduleDate = null;
              setIsScheduleDayBefore(true);
            } else {
              scheduleDate = requestData?.scheduleAt;
            }
          }

          let name = requestData?.stops[0]?.name;
          let regex = /\((.*?)\)/;
          let remark = name.match(regex);
          let cleanedName = name.replace(/\s*\([^)]*\)/g, "");

          setQuotationPayload((prev) => ({
            ...prev,
            ...requestData,
            item: {
              ...requestData?.item,
              quantity: Number(requestData?.item?.quantity),
            },
            scheduleAt: scheduleDate,
            serviceType:
              citiesData?.services?.find((item) => {
                return item?.key === requestData?.serviceType;
              }) || null,
            stops: [
              {
                name: cleanedName || "",
                phone: requestData?.stops[0]?.phone.replace("+66", "0") || "",
                coordinates: {
                  lat: requestData?.stops[0]?.coordinates?.lat,
                  lng: requestData?.stops[0]?.coordinates?.lng,
                },
                address: requestData?.stops[0]?.address || "",
                remark: remark && remark[1] ? remark[1] : "",
              },
            ],
          }));
        }
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderDetail]);

  const fetchCities = async () => {
    try {
      setIsLoading(true);
      const { data: res } = await getCityInfo();
      if (!!res) {
        // setCitiesData(res.data);
        let bangkokData = res.find((item) => item?.name === "Bangkok");
        let formatData = {
          ...bangkokData,
          services: bangkokData?.services?.filter(
            (item) => !removeServiceType.includes(item?.key),
          ),
        };
        setSelectedCities(formatData);

        return formatData;
      }
    } catch (error) {
      console.error("err: ", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteImg = (imgIndex) => {
    setShippingDetail((prevState) => {
      const updateShipping = [...prevState.fulfillmentProcess];
      const updateDeliveryProof = [...updateShipping[0].deliveryProofs];

      updateDeliveryProof.splice(imgIndex, 1);

      updateShipping[0] = {
        ...updateShipping[0],
        ...{ deliveryProofs: updateDeliveryProof },
      };
      return {
        ...prevState,
        fulfillmentProcess: updateShipping,
      };
    });
  };

  /**
   *
   * @note - this function is used to check if the quotation is changed
   * @param {*} oldValue - key of value in shippingDetail.fulfillmentProcess[0].lalamoveCheck.request to check
   * @param {*} changedValue - new value to compare with oldValue
   */
  const checkIsQuotationChange = (oldValue, changedValue) => {
    if (changedValue !== oldValue) {
      setIsQuotationChanged(true);
    } else {
      setIsQuotationChanged(false);
    }
  };

  const handleSchedule = (e) => {
    if (e.target.value === "schedule") {
      openDialog({
        type: "custom",
        customDialog: (
          <SelectDateDialog
            onClose={() => closeDialog()}
            currentDate={quotationPayload?.scheduleAt}
            callbackDate={(value) => {
              setQuotationPayload({
                ...quotationPayload,
                scheduleAt: value,
              });
              checkIsQuotationChange(
                shippingDetail?.fulfillmentProcess[0]?.lalamoveCheck?.request
                  ?.scheduleAt,
                value,
              );
              closeDialog();
            }}
          />
        ),
      });
    } else {
      setQuotationPayload({
        ...quotationPayload,
        scheduleAt: null,
      });
      checkIsQuotationChange(
        shippingDetail?.fulfillmentProcess[0]?.lalamoveCheck?.request
          ?.scheduleAt,
        null,
      );
    }
  };

  const onGetQuotation = async () => {
    try {
      setIsLoading(true);
      const { scheduleAt, ...payload } = quotationPayload;
      const { weight, ...item } = payload?.item;
      const quoInfo = {
        ...payload,
        ...(!!scheduleAt ? { scheduleAt } : {}),
        serviceType: payload.serviceType.key,
        item: {
          ...item,
          ...(!!weight ? { weight } : {}),
          quantity: item?.quantity.toString(),
        },
        stops: payload.stops.map(({ remark, ...stop }) => ({
          ...stop,
          name: `${stop.name}${remark ? ` (${remark})` : ""}`,
          phone: stop.phone.replaceAll("-", "").replace(stop.phone[0], "+66"),
          coordinates: {
            lat: stop.coordinates.lat.toString(),
            lng: stop.coordinates.lng.toString(),
          },
        })),
      };

      const { data: res } = await getQuotationInfo({ data: quoInfo });

      if (!!res) {
        setShippingDetail((prev) => {
          const updateShipping = [...prev.fulfillmentProcess];
          updateShipping[0] = {
            ...updateShipping[0],
            ...{
              lalamoveCheck: {
                request: res?.request,
                result: res?.result,
              },
            },
          };
          return {
            ...prev,
            fulfillmentProcess: updateShipping,
          };
        });

        setIsQuotationChanged(false);
        setIsScheduleDayBefore(false);
        setIsLalamoveFailed(false);
      }
    } catch (error) {
      console.error(error.response.data);

      error.response.data.errors.forEach((err) => {
        enqueueSnackbar(err.message, {
          variant: "error",
          autoHideDuration: 2000,
        });
      });
    } finally {
      setIsLoading(false);
    }
  };

  const onSubmit = async () => {
    try {
      setIsLoading(true);
      let shippingInfo = { ...shippingDetail };

      if (
        shippingDetail?.fulfillmentProcess[0]?.carrier === "Lalamove ส่งฟรี"
      ) {
        const { scheduleAt, ...payload } = quotationPayload;
        const { weight, ...item } = payload?.item;
        const quoInfo = {
          ...payload,
          ...(!!scheduleAt ? { scheduleAt } : {}),
          orderId: orderDetail?.order?._id || null,
          serviceType: payload.serviceType.key,
          item: {
            ...item,
            ...(!!weight ? { weight } : {}),
            quantity: item?.quantity.toString(),
          },
          stops: payload.stops.map(({ remark, ...stop }) => ({
            ...stop,
            name: `${stop.name}${remark ? ` (${remark})` : ""}`,
            phone: stop.phone.replaceAll("-", "").replace(stop.phone[0], "+66"),
            coordinates: {
              lat: stop.coordinates.lat.toString(),
              lng: stop.coordinates.lng.toString(),
            },
          })),
        };

        const { data: res } = await placeOrder({ data: quoInfo });

        if (!!res) {
          const updateShipping = [...shippingDetail.fulfillmentProcess];
          updateShipping[0] = {
            ...updateShipping[0],
            ...{
              lalamoveRequest: {
                request: res?.request,
                result: res?.result?.data,
              },
            },
            trackingUrl: res?.result?.data?.shareLink,
          };

          shippingInfo = {
            ...shippingDetail,
            fulfillmentProcess: updateShipping,
          };
        }
      }

      const payload = {
        ...shippingInfo,
        status: {
          value:
            shippingDetail?.fulfillmentProcess[0]?.carrier === "Lalamove ส่งฟรี"
              ? shippingDetail?.status?.value
              : "delivering",
        },
        order: orderDetail.order._id,
      };
      delete payload.orderDetail;

      const data = new FormData();
      for (const field in payload) {
        if (
          field === "order" ||
          field === "_id" ||
          field === "method" ||
          field === "note" ||
          field === "seller"
        ) {
          data.append(field, payload[field]);
        } else {
          data.append(field, JSON.stringify(payload[field]));
        }
      }

      if (payload?.fulfillmentProcess?.[0]?.deliveryProofs?.length > 0) {
        // eslint-disable-next-line array-callback-return
        payload?.fulfillmentProcess?.[0]?.deliveryProofs.map((item, index) => {
          if (item instanceof File) {
            return data.append(
              `fulfillmentProcess.0.deliveryProofs.${index}.deliveryProof`,
              item,
            );
          }
        });
      }
      delete data.documents;

      let fulfillmentProcess = JSON.parse(data.get("fulfillmentProcess"));
      if (fulfillmentProcess?.[0]?.deliveryProofs?.length > 0) {
        let formatDeliveryProofs = fulfillmentProcess?.[0]?.deliveryProofs;

        // eslint-disable-next-line array-callback-return
        formatDeliveryProofs.map((item, index) => {
          if (item instanceof File) {
            data.append(
              `fulfillmentProcess.0.deliveryProofs.${index}.deliveryProof`,
              item,
            );
          } else {
            let format = item;
            format.deliveryProof = item?.deliveryProof?._id;
            return format;
          }
        });

        fulfillmentProcess[0] = {
          ...fulfillmentProcess[0],
          ...{ deliveryProofs: formatDeliveryProofs },
        };
      }
      // delete fulfillmentProcess[0]?.deliveryProofs;
      data.set("fulfillmentProcess", JSON.stringify(fulfillmentProcess));

      await updateShippingOrder(shippingDetail._id, data);
      await onSuccess();
      enqueueSnackbar(`อัพเดทรายละเอียดการจัดส่งสินค้า สำเร็จ`, {
        variant: "success",
      });
    } catch (err) {
      console.error("err:", err);
      // enqueueSnackbar(err?.response?.data?.message, {
      //   variant: "error",
      // });
    } finally {
      setIsLoading(false);
    }
  };

  const validateField = () => {
    if (shippingDetail?.method === "ส่งไปถึงลูกค้าโดยตรง") {
      if (
        shippingDetail?.fulfillmentProcess?.[0]?.carrier === "จัดส่งโดยร้านค้า"
      ) {
        if (
          shippingDetail?.fulfillmentProcess?.[0]?.estimatedDeliveryDate
          // && shippingDetail?.fulfillmentProcess?.[0]?.deliveryProofs
        )
          return false;
        else return true;
      } else if (
        shippingDetail?.fulfillmentProcess?.[0]?.carrier === "Lalamove"
      ) {
        if (
          shippingDetail?.fulfillmentProcess?.[0]?.trackingUrl &&
          shippingDetail?.fulfillmentProcess?.[0]?.estimatedDeliveryDate
          // && shippingDetail?.fulfillmentProcess?.[0]?.deliveryProofs
        )
          return false;
        else return true;
      } else if (
        shippingDetail?.fulfillmentProcess?.[0]?.carrier === "Lalamove ส่งฟรี"
      ) {
        if (
          shippingDetail?.fulfillmentProcess?.[0]?.estimatedDeliveryDate &&
          quotationPayload?.serviceType &&
          quotationPayload?.item?.quantity &&
          quotationPayload?.stops?.[0]?.coordinates?.lat &&
          quotationPayload?.stops?.[0]?.coordinates?.lng &&
          quotationPayload?.stops?.[0]?.address &&
          !isQuotationChanged &&
          !isScheduleDayBefore &&
          !isLalamoveFailed
          // && shippingDetail?.fulfillmentProcess?.[0]?.deliveryProofs
        )
          return false;
        else return true;
      } else {
        if (
          shippingDetail?.fulfillmentProcess?.[0]?.trackingNumber &&
          shippingDetail?.fulfillmentProcess?.[0]?.estimatedDeliveryDate
          // && shippingDetail?.fulfillmentProcess?.[0]?.deliveryProofs
        )
          return false;
        else return true;
      }
    } else if (shippingDetail?.method === "ส่งไปที่ขนส่ง") {
      if (
        shippingDetail?.fulfillmentProcess?.[0]?.carrier === "จัดส่งโดยร้านค้า"
      ) {
        if (
          shippingDetail?.fulfillmentProcess?.[1]?.trackingNumber &&
          shippingDetail?.fulfillmentProcess?.[0]?.estimatedDeliveryDate
          // && shippingDetail?.fulfillmentProcess?.[0]?.deliveryProofs
        )
          return false;
        else return true;
      } else if (
        shippingDetail?.fulfillmentProcess?.[0]?.carrier === "Lalamove"
      ) {
      } else if (
        shippingDetail?.fulfillmentProcess?.[0]?.carrier === "อื่นๆ" &&
        shippingDetail?.fulfillmentProcess?.[1]?.carrier === "อื่นๆ"
      ) {
        if (
          shippingDetail?.fulfillmentProcess?.[0]?.trackingNumber &&
          shippingDetail?.fulfillmentProcess?.[1]?.trackingNumber &&
          shippingDetail?.fulfillmentProcess?.[0]?.estimatedDeliveryDate
          // && shippingDetail?.fulfillmentProcess?.[0]?.deliveryProofs
        )
          return false;
        else return true;
      }
    } else {
      // if (shippingDetail?.fulfillmentProcess?.[0]?.deliveryProofs) return false;
      // else return true;
      return false;
    }
  };

  return (
    <StyledDialog
      onClose={onClose}
      aria-labelledby="customized-dialog-title"
      open={true}
    >
      <DialogTitle
        sx={{
          p: {
            sm: 3,
            xs: 2,
          },
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
        }}
      >
        <Grid container alignItems="center">
          <Grid
            items
            sx={{
              fontSize: {
                sm: font.size.lg,
                xs: font.size.md,
              },
              fontWeight: 600,
              marginRight: "5px",
            }}
          >
            การจัดส่ง
          </Grid>
          <SpanLabel>{`${
            isJSONString(shippingDetail?.method) === "ส่งไปถึงลูกค้าโดยตรง"
              ? `- ${shippingDetail?.fulfillmentProcess[0]?.carrier}`
              : isJSONString(shippingDetail?.method) === "ลูกค้ารับเอง"
                ? `- ${isJSONString(shippingDetail?.method)}`
                : ""
          }`}</SpanLabel>
        </Grid>

        {/* <SpanLabel isMargin>วันที่ส่งสำเร็จ</SpanLabel> */}
        <CloseButton aria-label="close" onClick={onClose}>
          <CloseIcon fontSize="small" />
        </CloseButton>
      </DialogTitle>

      <DialogContent>
        <Grid container>
          {isJSONString(shippingDetail?.method) === "ส่งไปถึงลูกค้าโดยตรง" &&
            shippingDetail?.fulfillmentProcess[0]?.carrier === "Lalamove" && (
              <>
                <Grid item xs={12} container>
                  <SpanLabel isMargin>
                    {`Link ติดตามสินค้าของ Lalamove`} <span>*</span>
                  </SpanLabel>
                  <TextField
                    variant="outlined"
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    value={shippingDetail?.fulfillmentProcess[0]?.trackingUrl}
                    onChange={({ target }) =>
                      setShippingDetail((prevState) => {
                        const updateShipping = [
                          ...prevState.fulfillmentProcess,
                        ];
                        updateShipping[0] = {
                          ...updateShipping[0],
                          ...{ trackingUrl: target.value },
                        };
                        return {
                          ...prevState,
                          fulfillmentProcess: updateShipping,
                        };
                      })
                    }
                  />
                </Grid>

                <Grid item xs={12}>
                  <Line />
                </Grid>
              </>
            )}

          {isJSONString(shippingDetail?.method) === "ส่งไปถึงลูกค้าโดยตรง" &&
            shippingDetail?.fulfillmentProcess[0]?.carrier === "อื่นๆ" && (
              <>
                <Grid item xs={12} container>
                  <SpanLabel isMargin>
                    {`เลขพัสดุ`} <span>*</span>
                  </SpanLabel>
                  <TextField
                    variant="outlined"
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    value={
                      shippingDetail?.fulfillmentProcess[0]?.trackingNumber
                    }
                    onChange={({ target }) =>
                      setShippingDetail((prevState) => {
                        const updateShipping = [
                          ...prevState.fulfillmentProcess,
                        ];
                        updateShipping[0] = {
                          ...updateShipping[0],
                          ...{ trackingNumber: target.value },
                        };
                        return {
                          ...prevState,
                          fulfillmentProcess: updateShipping,
                        };
                      })
                    }
                  />
                </Grid>

                <Grid item xs={12}>
                  <Line />
                </Grid>
              </>
            )}

          {/* //!! Lalamove ส่งฟรี */}
          {isJSONString(shippingDetail?.method) === "ส่งไปถึงลูกค้าโดยตรง" &&
            shippingDetail?.fulfillmentProcess[0]?.carrier ===
              "Lalamove ส่งฟรี" && (
              <>
                <Grid item xs={12} container spacing={2} mb={2}>
                  <Grid item xs={12}>
                    <SpanLabel isMargin>
                      {`วันที่คาดว่าจะส่งถึง`} <span>*</span>
                    </SpanLabel>

                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <StyledDatePicker
                        format="DD-MM-YYYY"
                        value={
                          shippingDetail?.fulfillmentProcess[0]
                            ?.estimatedDeliveryDate
                            ? dayjs(
                                shippingDetail?.fulfillmentProcess[0]
                                  ?.estimatedDeliveryDate,
                              )
                            : null
                        }
                        onChange={(value) => {
                          setShippingDetail((prevState) => {
                            const updateShipping = [
                              ...prevState.fulfillmentProcess,
                            ];
                            updateShipping[0] = {
                              ...updateShipping[0],
                              ...{ estimatedDeliveryDate: value.$d },
                            };
                            return {
                              ...prevState,
                              fulfillmentProcess: updateShipping,
                            };
                          });
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            variant="outlined"
                            sx={{
                              width: "100%",
                            }}
                          />
                        )}
                      />
                    </LocalizationProvider>
                  </Grid>

                  {/* Recipient Location */}
                  <Grid item xs={12}>
                    <SpanLabel isMargin>ที่อยู่จัดส่ง (ปลายทาง)</SpanLabel>

                    <Accordion>
                      <AccordionSummary
                        expandIcon={<ExpandMoreIcon />}
                        aria-controls="recipient-content"
                        id="recipient-header"
                      >
                        {/* Recipient Address */}
                        {
                          shippingDetail?.order?.shippingAddress?.recipients
                            ?.address
                        }
                      </AccordionSummary>

                      <AccordionDetails>
                        <Grid container spacing={2}>
                          {/* Recipient Coordinates */}
                          <Grid item xs={12}>
                            <WrapperMapsContainer>
                              <MapsContainer>
                                <CustomMap
                                  markerLocation={{
                                    lat: shippingDetail?.order?.shippingAddress
                                      ?.recipients?.coordinates?.lat,
                                    lng: shippingDetail?.order?.shippingAddress
                                      ?.recipients?.coordinates?.lng,
                                  }}
                                  isViewOnly
                                />
                              </MapsContainer>
                            </WrapperMapsContainer>
                          </Grid>
                        </Grid>
                      </AccordionDetails>
                    </Accordion>
                  </Grid>

                  <Grid item xs={12}>
                    <SpanLabel>
                      จุดรับสินค้า (คลังสินค้า)<span>*</span>
                    </SpanLabel>
                  </Grid>

                  {/* Sender Address (cannot edit) */}
                  <Grid item xs={12}>
                    <TextField
                      label="ที่อยู่คลังสินค้าสำหรับเข้ารับสินค้า"
                      variant="outlined"
                      fullWidth
                      disabled
                      value={quotationPayload?.stops[0]?.address}
                      onChange={(e) => {
                        let stopData = quotationPayload.stops;
                        stopData[0].address = e.target.value;
                        setQuotationPayload({
                          ...quotationPayload,
                          stops: stopData,
                        });

                        checkIsQuotationChange(
                          shippingDetail?.fulfillmentProcess[0]?.lalamoveCheck
                            ?.request?.stops[0]?.address,
                          e.target.value,
                        );
                      }}
                      error={!!!quotationPayload?.stops[0]?.address}
                      helperText={
                        !!!quotationPayload?.stops[0]?.address
                          ? `กรุณากรอกที่อยู่`
                          : ``
                      }
                    />
                  </Grid>

                  {/* Sender Name */}
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="ชื่อผู้ส่ง"
                      variant="outlined"
                      fullWidth
                      value={quotationPayload?.stops[0]?.name || ""}
                      onChange={(e) => {
                        let stopData = quotationPayload.stops;
                        stopData[0].name = e.target.value;
                        setQuotationPayload({
                          ...quotationPayload,
                          stops: stopData,
                        });
                      }}
                    />
                  </Grid>

                  {/* Sender Phone */}
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="เบอร์โทรผู้ส่ง"
                      placeholder="เฉพาะตัวเลข 10 หลัก"
                      variant="outlined"
                      fullWidth
                      value={quotationPayload?.stops[0]?.phone || ""}
                      onChange={(e) => {
                        let stopData = quotationPayload.stops;
                        stopData[0].phone = e.target.value;
                        setQuotationPayload({
                          ...quotationPayload,
                          stops: stopData,
                        });
                      }}
                    />
                  </Grid>

                  {/* Remark */}
                  <Grid item xs={12}>
                    <TextField
                      id="remark-input"
                      label="หมายเหตุถึงคนขับ"
                      placeholder="หมายเหตุถึงคนขับ (ไม่บังคับ)"
                      inputProps={{ maxLength: 30 }}
                      fullWidth
                      onChange={(e) => {
                        let stopData = quotationPayload.stops;
                        stopData[0].remark = e.target.value;
                        setQuotationPayload({
                          ...quotationPayload,
                          stops: stopData,
                        });
                      }}
                      value={quotationPayload?.stops[0]?.remark || ""}
                      helperText="Max 30 characters"
                    />
                  </Grid>

                  {/* Service type */}
                  <Grid item xs={12} sm={6}>
                    <Autocomplete
                      // freeSolo
                      fullWidth
                      aria-required={true}
                      id="service-type-select"
                      getOptionSelected={(option, value) =>
                        option?.key === value?.key
                      }
                      isOptionEqualToValue={(option, value) =>
                        option?.key === value?.key
                      }
                      options={selectedCities?.services || []}
                      value={quotationPayload?.serviceType}
                      renderOption={(props, option, { selected }) => (
                        <li
                          {...props}
                          key={`service-type-select-${option?.key}`}
                        >
                          <Box
                            sx={{
                              width: "100%",
                              py: 1,
                              display: "flex",
                              alignItems: "center",
                              // justifyContent: "space-around",
                              gap: 2,
                            }}
                          >
                            <Box
                              sx={{
                                width: "40px",
                                height: "40px",
                                display: "flex",
                                alignItems: "center",
                              }}
                            >
                              <img
                                style={{
                                  width: "100%",
                                  height: "auto",
                                  objectFit: "cover",
                                  objectPosition: "center",
                                  borderRadius: "8px",
                                }}
                                src={`https://xsurface-images.s3.ap-southeast-1.amazonaws.com/lalamove-icon/${option?.key}.png`}
                                alt={`${option?.key}-img`}
                              />
                            </Box>

                            <Box>
                              <TextOption size="title">
                                {serviceType(option?.key)}
                              </TextOption>

                              <TextOption>
                                {`ขนาดสินค้าแนะนำ : ${option?.dimensions?.length?.value} x ${option?.dimensions?.width?.value} x ${option?.dimensions?.height?.value} เมตร`}
                              </TextOption>
                              <TextOption>
                                {`น้ำหนักสูงสุด ${option?.load?.value} กิโลกรัม`}
                              </TextOption>
                            </Box>
                          </Box>
                        </li>
                      )}
                      onChange={(_, newValue) => {
                        if (!!newValue) {
                          setQuotationPayload({
                            ...quotationPayload,
                            serviceType: newValue,
                            specialRequests: [],
                          });

                          checkIsQuotationChange(
                            shippingDetail?.fulfillmentProcess[0]?.lalamoveCheck
                              ?.request?.serviceType,
                            newValue.key,
                          );
                        } else {
                          setQuotationPayload({
                            ...quotationPayload,
                            serviceType: null,
                            specialRequests: [],
                          });
                          setIsQuotationChanged(true);
                        }
                      }}
                      sx={{
                        "& .MuiOutlinedInput-root .MuiAutocomplete-input": {
                          py: "3px",
                        },
                      }}
                      getOptionLabel={(option) => serviceType(option?.key)}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          fullWidth
                          label="เลือกประเภทรถ"
                        />
                      )}
                    />
                    <Stack
                      alignItems="center"
                      direction="row"
                      // justifyContent="center"
                      spacing={1}
                      sx={{ mt: 1 }}
                    >
                      <InfoIcon style={{ color: Yellow }} />
                      <RemarkText>
                        กรุณาเลือกประเภทรถให้เข้ากับสินค้า
                      </RemarkText>
                    </Stack>
                  </Grid>

                  {/* Schedule date */}
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth>
                      <InputLabel id="schedule-date-label">
                        เวลาเข้ารับสินค้า
                      </InputLabel>
                      <Select
                        labelId="schedule-date-label"
                        id="schedule-date"
                        label="เวลาเข้ารับสินค้า"
                        value={
                          !!quotationPayload?.scheduleAt
                            ? quotationPayload?.scheduleAt
                            : "-"
                        }
                        sx={{
                          "& .MuiSelect-select": {
                            padding: "12px 16px",
                          },
                        }}
                        onChange={handleSchedule}
                      >
                        <MenuItem value={"-"}>รับทันที</MenuItem>
                        <MenuItem value={"schedule"}>ภายหลัง</MenuItem>
                        <MenuItem
                          sx={{ display: "none" }}
                          value={
                            !!quotationPayload?.scheduleAt &&
                            quotationPayload?.scheduleAt
                          }
                        >
                          {dayjs(quotationPayload?.scheduleAt).format(
                            "DD/MM/YYYY - hh:mm A",
                          )}
                        </MenuItem>
                      </Select>
                    </FormControl>
                    {isScheduleDayBefore && (
                      <Stack
                        alignItems="center"
                        direction="row"
                        // justifyContent="center"
                        spacing={1}
                        sx={{ mt: 1 }}
                      >
                        <InfoIcon style={{ color: Primary }} />
                        <WarningText>
                          ขณะนี้เกินจากเวลาเข้ารับสินค้าที่เลือกไว้
                          กรุณาเลือกวันที่และเวลาใหม่อีกครั้ง
                        </WarningText>
                      </Stack>
                    )}
                  </Grid>
                </Grid>

                <Grid item xs={12} container spacing={3}>
                  {/* volume */}
                  <Grid item xs={12}>
                    <Stack direction="row" spacing={3}>
                      <SpanLabel>
                        จำนวนพัสดุ<span>*</span>
                      </SpanLabel>

                      <QuantityInput
                        onChange={(value) => {
                          let itemDetail = quotationPayload?.item;
                          itemDetail.quantity = value;
                          setQuotationPayload({
                            ...quotationPayload,
                            item: itemDetail,
                          });

                          checkIsQuotationChange(
                            Number(
                              shippingDetail?.fulfillmentProcess[0]
                                ?.lalamoveCheck?.request?.item?.quantity,
                            ),
                            value,
                          );
                        }}
                        value={quotationPayload?.item?.quantity}
                      />
                    </Stack>
                  </Grid>

                  {/* get quotation */}
                  {(isQuotationChanged ||
                    isScheduleDayBefore ||
                    isLalamoveFailed) && (
                    <>
                      <Grid
                        item
                        xs={12}
                        // sm={6}
                        // md={5}
                        sx={{ textAlign: "center" }}
                      >
                        {isScheduleDayBefore ? (
                          <FormHelperText
                            error={true}
                            sx={{ textAlign: "center", mb: 1 }}
                          >
                            ** ขณะนี้เกินจากเวลาเข้ารับสินค้าที่เลือกไว้
                            กรุณาตรวจสอบรายการใหม่อีกครั้ง **
                          </FormHelperText>
                        ) : (
                          isQuotationChanged && (
                            <>
                              <FormHelperText
                                error={true}
                                sx={{ textAlign: "center", mb: 0.5 }}
                              >
                                ⚠️ แจ้งเตือนการเปลี่ยนประเภทการจัดส่ง ⚠️
                              </FormHelperText>

                              <FormHelperText
                                error={true}
                                sx={{ textAlign: "center", mb: 2 }}
                              >
                                หากร้านค้าดำเนินการเปลี่ยนรูปแบบการจัดส่งหลังสถานะ
                                “เตรียมจัดส่ง” และค่าจัดส่งใหม่มากกว่า xsurface
                                กำหนด (เกิน 800 บาท)
                                ร้านค้าจะต้องเป็นผู้รับผิดชอบค่าจัดส่งส่วนเกินทั้งหมด
                              </FormHelperText>
                            </>
                          )
                        )}

                        <Button
                          variant="secondary"
                          // size="large"
                          onClick={() => {
                            onGetQuotation();
                          }}
                          sx={{
                            minWidth: 160,
                            width: "max-content",
                          }}
                          startIcon={<LocalShippingIcon />}
                        >
                          ตรวจสอบรายการ
                        </Button>
                      </Grid>
                    </>
                  )}

                  {!isQuotationChanged && !isLalamoveFailed ? (
                    <>
                      <Grid item xs={12}>
                        <WrapperCalOrder>
                          <RowSumPrice>
                            <Price desc>ค่าจัดส่ง</Price>
                            <Price>
                              {convertPrice(
                                Number(
                                  shippingDetail.fulfillmentProcess[0]
                                    ?.lalamoveCheck?.result?.totalWithFee,
                                ),
                              )}
                            </Price>
                          </RowSumPrice>

                          {/* <RowSumPrice>
                        <Price desc>ค่าบริการคำร้องขอเพิ่มเติม</Price>
                        <Price>
                          {convertPrice(
                            orderPayload?.lalamove_quotation?.priceBreakdown
                              ?.specialRequests || 0,
                          )}
                        </Price>
                      </RowSumPrice>
  
                      <RowSumPrice>
                        <Price desc total>
                          รวม
                        </Price>
                        <Price total>
                          {convertPrice(
                            orderPayload?.lalamove_quotation?.priceBreakdown
                              ?.total,
                          )}
                        </Price>
                      </RowSumPrice> */}
                        </WrapperCalOrder>
                      </Grid>
                    </>
                  ) : null}
                </Grid>

                <Grid item xs={12}>
                  <Line />
                </Grid>
              </>
            )}

          {isJSONString(shippingDetail?.method) === "ส่งไปที่ขนส่ง" && (
            <>
              <Grid item xs={12} container>
                <SpanLabel highlight>
                  การส่งของที่ 1 (ร้านค้าส่งไปขนส่ง)
                </SpanLabel>
                <SpanLabel isMargin>
                  {`- ${shippingDetail?.fulfillmentProcess[0]?.carrier}`}
                </SpanLabel>
              </Grid>

              {shippingDetail?.fulfillmentProcess[0]?.carrier ===
                "Lalamove" && (
                <Grid item container xs={12} mb={2}>
                  <SpanLabel isMargin>
                    {`Link ติดตามสินค้าของ Lalamove`} <span>*</span>
                  </SpanLabel>
                  <TextField
                    variant="outlined"
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    value={shippingDetail?.fulfillmentProcess[0]?.trackingUrl}
                    onChange={({ target }) =>
                      setShippingDetail((prevState) => {
                        const updateShipping = [
                          ...prevState.fulfillmentProcess,
                        ];
                        updateShipping[0] = {
                          ...updateShipping[0],
                          ...{ trackingUrl: target.value },
                        };
                        return {
                          ...prevState,
                          fulfillmentProcess: updateShipping,
                        };
                      })
                    }
                  />
                </Grid>
              )}

              {shippingDetail?.fulfillmentProcess?.[0]?.carrier === "อื่นๆ" && (
                <Grid item container xs={12} mb={2}>
                  <SpanLabel isMargin>
                    {`เลขพัสดุ`} <span>*</span>
                  </SpanLabel>
                  <TextField
                    variant="outlined"
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    value={
                      shippingDetail?.fulfillmentProcess?.[0]?.trackingNumber
                    }
                    onChange={({ target }) =>
                      setShippingDetail((prevState) => {
                        const updateShipping = [
                          ...prevState.fulfillmentProcess,
                        ];
                        updateShipping[0] = {
                          ...updateShipping[0],
                          ...{ trackingNumber: target.value },
                        };
                        return {
                          ...prevState,
                          fulfillmentProcess: updateShipping,
                        };
                      })
                    }
                  />
                </Grid>
              )}

              <Grid item container xs={12}>
                <SpanLabel highlight>
                  การส่งของที่ 2 (ขนส่งส่งไปลูกค้า)
                </SpanLabel>
                <SpanLabel isMargin>
                  {`- ${shippingDetail?.fulfillmentProcess?.[1]?.carrier}`}
                </SpanLabel>
              </Grid>

              <Grid item container xs={12} mb={1}>
                <SpanLabel isMargin>
                  {`เลขพัสดุ`} <span>*</span>
                </SpanLabel>
                <TextField
                  variant="outlined"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  value={
                    shippingDetail?.fulfillmentProcess?.[1]?.trackingNumber
                  }
                  onChange={({ target }) =>
                    setShippingDetail((prevState) => {
                      const updateShipping = [...prevState.fulfillmentProcess];
                      updateShipping[1] = {
                        ...updateShipping[1],
                        ...{ trackingNumber: target.value },
                      };
                      return {
                        ...prevState,
                        fulfillmentProcess: updateShipping,
                      };
                    })
                  }
                />
              </Grid>

              <Line />
            </>
          )}

          {shippingDetail?.method !== "ลูกค้ารับเอง" &&
            shippingDetail?.fulfillmentProcess[0]?.carrier !==
              "Lalamove ส่งฟรี" && (
              <Grid item xs={12} container>
                <Grid item xs={12}>
                  <SpanLabel isMargin>
                    {`คาดว่าจะได้รับสินค้าภายในวันที่`} <span>*</span>
                  </SpanLabel>
                </Grid>

                <Grid item xs={12} mb={2}>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <StyledDatePicker
                      format="DD-MM-YYYY"
                      value={
                        shippingDetail?.fulfillmentProcess[0]
                          ?.estimatedDeliveryDate
                          ? dayjs(
                              shippingDetail?.fulfillmentProcess[0]
                                ?.estimatedDeliveryDate,
                            )
                          : null
                      }
                      onChange={(value) => {
                        setShippingDetail((prevState) => {
                          const updateShipping = [
                            ...prevState.fulfillmentProcess,
                          ];
                          updateShipping[0] = {
                            ...updateShipping[0],
                            ...{ estimatedDeliveryDate: value.$d },
                          };
                          return {
                            ...prevState,
                            fulfillmentProcess: updateShipping,
                          };
                        });
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          variant="outlined"
                          sx={{
                            width: "100%",
                          }}
                        />
                      )}
                    />
                  </LocalizationProvider>
                </Grid>
              </Grid>
            )}

          <Grid
            item
            xs={12}
            container
            spacing={1}
            justifyContent="space-between"
          >
            <Grid item xs={12}>
              <SpanLabel>
                รูปแพ็คสินค้าก่อนจัดส่ง <span>(สูงสุด 3 รูป)</span>
              </SpanLabel>
            </Grid>

            <Grid item xs={12}>
              <UploadMultiImage
                callBackFile={(fileCallBlack) => {
                  fileCallBlack !== "" &&
                    setShippingDetail((prevState) => {
                      const updateShipping = [...prevState.fulfillmentProcess];
                      let updateDeliveryProof = [
                        ...(updateShipping[0].deliveryProofs || []),
                      ];

                      if (
                        updateShipping[0].deliveryProofs?.length +
                          fileCallBlack?.length >
                        3
                      ) {
                        enqueueSnackbar(`Number of files is more than 3.`, {
                          variant: "error",
                        });
                      } else {
                        updateDeliveryProof = [
                          ...updateDeliveryProof,
                          ...fileCallBlack,
                        ];
                      }

                      updateShipping[0] = {
                        ...updateShipping[0],
                        ...{ deliveryProofs: updateDeliveryProof },
                      };
                      return {
                        ...prevState,
                        fulfillmentProcess: updateShipping,
                      };
                    });
                }}
                closeAlert={false}
                limitFile={3}
                isDisabled={
                  shippingDetail?.fulfillmentProcess?.[0]?.deliveryProofs
                    ?.length >= 3
                }
              >
                <Dragger>
                  <WrapperIconUpload>
                    <FileUploadIcon sx={{ color: Black }} />
                  </WrapperIconUpload>
                  <TextDragger>
                    <span>กดเพื่ออัปโหลด</span> หรือลากวาง
                  </TextDragger>
                  <TextDragger>jpg, png, jpeg</TextDragger>
                  <TextDragger>(Max. File size: 4 MB)</TextDragger>
                </Dragger>
              </UploadMultiImage>
            </Grid>

            {checkNotEmpty(
              shippingDetail?.fulfillmentProcess?.[0]?.deliveryProofs,
            ) && (
              <Grid item xs={12} container spacing={1}>
                {shippingDetail?.fulfillmentProcess?.[0]?.deliveryProofs.map(
                  (img, index) => (
                    <Grid item xs={12}>
                      <UploadItem>
                        <Grid
                          container
                          spacing={{ xs: 1, md: 2 }}
                          alignItems="center"
                        >
                          <Grid
                            item
                            xs="auto"
                            sx={{
                              maxHeight: { xs: "68px", sm: "88px", md: "96px" },
                            }}
                          >
                            <PreviewImage
                              src={
                                img instanceof File
                                  ? img?.fileData
                                  : img?.deliveryProof?.docUrl
                              }
                              alt={`deliveryProof-${index}`}
                            />
                          </Grid>

                          <Grid item xs>
                            <PreviewImageTitle>
                              {img instanceof File
                                ? img?.path
                                : img?.deliveryProof?.originalFileName}
                            </PreviewImageTitle>
                          </Grid>

                          <Grid item xs="auto">
                            <IconButton onClick={() => handleDeleteImg(index)}>
                              <DeleteIcon sx={{ color: Grey }} />
                            </IconButton>
                          </Grid>
                        </Grid>
                      </UploadItem>
                    </Grid>
                  ),
                )}
              </Grid>
            )}
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button
          variant="outlined"
          color="secondary"
          size="large"
          onClick={() => onClose()}
          fullWidth
          sx={{ maxWidth: 180 }}
        >
          ยกเลิก
        </Button>

        <Button
          variant="contained"
          size="large"
          onClick={() => onSubmit()}
          fullWidth
          sx={{ maxWidth: 180 }}
          disabled={validateField()}
        >
          ยืนยัน
        </Button>
      </DialogActions>
    </StyledDialog>
  );
};

export default DeliveringDialog;
